import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetch} from "services/query-client";
import {CashIcon} from "@heroicons/react/outline";
import Button from "components/Button";
import {CashIcon as CashIconSolid, EyeIcon, PencilIcon, PlusIcon, TrashIcon} from "@heroicons/react/solid";
import {useNavigate} from "react-router-dom";
import {Sponsorship} from "types/Sponsorship";
import Dropdown from "components/Dropdown";
import DeleteSponsorship from '../Delete';
import {toTitleCase} from "services/string";
import {formattedDate} from 'services/date';
import {useOperator} from "context/OperatorContext";
import useSorting from "../../../hooks/useSorting";
import SearchBar from "../../../components/SearchBar";
import {SortDirection} from "../../../types/SortDirection";

// Component ----

function SponsorshipList() {
  const {currentSort, queryString: sort, sortBy} = useSorting();
  const [search, setSearch] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);

  const result = useQuery(
    ['sponsorships.all', {search, sort, page, perPage: 25}],
    () => ngage.sponsorships.filtered({search, sort, page, perPage: 25}),
    {keepPreviousData: true}
  )

  const [deletingSponsorship, setDeletingSponsorship] = useState<Sponsorship | null>(null);

  const {termFor} = useOperator()
  const navigate = useNavigate()

  // Prefetch next and previous pages
  prefetch('sponsorships.all', {search, sort, page, perPage: 25}, ngage.sponsorships.all)

  // Prepare meta data
  const meta = [
    {icon: CashIconSolid, value: (result.data?.meta.all ?? 0).toLocaleString() + ' sold'}
  ]

  // Prepare page actions
  const actions = <Button icon={PlusIcon} onClick={() => navigate('/sponsorships/new')}>New</Button>

  // Prepare breadcrumbs
  const breadcrumbs = {
    'Commercial': '/',
    [termFor('Sponsorships')]: '/sponsorships'
  }

  return (
    <Page
      title={termFor('Sponsorships')}
      subTitle={`Recognise user and organisation ${termFor('sponsorships')}`}
      meta={meta} breadcrumbs={breadcrumbs}
      actions={actions}
    >
      {/* Search and filtering */}
      <div className="mb-4 w-full grid md:grid-cols-4">
        <SearchBar isSearching={result.isFetching} onChange={setSearch}/>
      </div>

      {/* Data table */}
      <Table paginationData={result.data} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader
              sortable
              sortDirection={currentSort.name ?? SortDirection.NONE}
              onSort={direction => sortBy('name', direction)}>
              Name
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.sponsorable ?? SortDirection.NONE}
              onSort={direction => sortBy('sponsorable', direction)}>
              Entity
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.tier ?? SortDirection.NONE}
              onSort={direction => sortBy('tier', direction)}>
              Tier
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.duration ?? SortDirection.NONE}
              onSort={direction => sortBy('duration', direction)}>
              Duration
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.starts_at ?? SortDirection.NONE}
              onSort={direction => sortBy('starts_at', direction)}>
              Starts At
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.ends_at ?? SortDirection.NONE}
              onSort={direction => sortBy('ends_at', direction)}>
              Ends At
            </TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={result.isLoading}>

          {result?.data && result.data.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={6}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <CashIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No {termFor('sponsorships')}</span>
                </div>
              </TableData>
            </TableRow>
            : result?.data?.data.map((sponsorship, index) => (
              <TableRow key={sponsorship.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      {/* TODO: Convert to component */}
                      <Dropdown position="left">
                        {[
                          {name: 'View', icon: EyeIcon, onClick: () => navigate(`/sponsorships/${sponsorship.id}`)},
                          {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/sponsorships/${sponsorship.id}/edit`)},
                          {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingSponsorship(sponsorship)}
                        ]}
                      </Dropdown>
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      <CashIcon className="h-5 w-5 mr-2 text-gray-500"/> {sponsorship.name}
                    </div>
                  </div>
                </TableData>
                <TableData>{`${sponsorship.sponsorable.name} (${toTitleCase(sponsorship.sponsorable_type)})`}</TableData>
                <TableData>{sponsorship.tier.name}</TableData>
                <TableData>{sponsorship.duration?.toHuman() ?? 'Indefinite'}</TableData>
                <TableData>{formattedDate(sponsorship.starts_at)}</TableData>
                <TableData>{formattedDate(sponsorship.ends_at)}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  {/* TODO: Convert to component */}
                  <Dropdown>
                    {[
                      {name: 'View', icon: EyeIcon, onClick: () => navigate(`/sponsorships/${sponsorship.id}`)},
                      {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/sponsorships/${sponsorship.id}/edit`)},
                      {name: 'Delete', icon: TrashIcon, onClick: () => setDeletingSponsorship(sponsorship)}
                    ]}
                  </Dropdown>
                </TableData>

                {/* Delete confirmation modal */}
                <DeleteSponsorship
                  sponsorship={deletingSponsorship}
                  show={sponsorship === deletingSponsorship}
                  onCancel={() => setDeletingSponsorship(null)}
                  onDelete={() => navigate('/sponsorships')}
                  onDeleteFail={() => setDeletingSponsorship(null)}
                />
              </TableRow>
            ))}

        </TableBody>
      </Table>
    </Page>
  );
}

export default SponsorshipList;